import type { App } from '../app/App';
import { newSave } from '../game/systems/model';
import { isKid } from '../game/systems/policy';
import { ApiError } from '../networking/ApiClient';
import { ensureOnline, goLive } from '../networking/session';
import { syncEntitlements } from '../networking/shop';
import { language, t } from '../i18n';
import { button, confirmBox, modal } from './components';
import { h } from './dom';
import { parentGate } from './family';

/* ---------- Google Identity Services (loaded only when needed) ---------- */

interface GoogleId {
  initialize(opts: { client_id: string; callback: (r: { credential: string }) => void; ux_mode?: 'popup'; auto_select?: boolean; itp_support?: boolean }): void;
  renderButton(el: HTMLElement, opts: Record<string, unknown>): void;
}
declare global {
  interface Window {
    google?: { accounts: { id: GoogleId } };
  }
}

let gis: Promise<GoogleId> | null = null;
let clientId: Promise<string | null> | null = null;

/** The server's Google client id, or null when Google sign-in is off (or the server is the Go dev server). */
export function googleClientId(app: App): Promise<string | null> {
  clientId ??= app.api.config().then(
    (c) => c.googleClientId,
    () => {
      clientId = null; // try again next time
      return null;
    },
  );
  return clientId;
}

function loadGis(): Promise<GoogleId> {
  gis ??= new Promise<GoogleId>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = () => (window.google ? resolve(window.google.accounts.id) : reject(new Error('Google sign-in did not load')));
    s.onerror = () => {
      gis = null;
      reject(new Error('Google sign-in did not load'));
    };
    document.head.appendChild(s);
  });
  return gis;
}

/** Draws Google's own "Sign in with Google" button into a new element; onCredential gets the ID token. */
function googleButton(id: string, onCredential: (credential: string) => void): HTMLElement {
  const box = h('div', { class: 'google-btn' }, h('span', { class: 'hand-hint' }, t('Loading Google…')));
  loadGis().then(
    (g) => {
      g.initialize({ client_id: id, callback: (r) => onCredential(r.credential), ux_mode: 'popup', auto_select: false, itp_support: true });
      box.replaceChildren();
      g.renderButton(box, { theme: 'outline', size: 'large', shape: 'pill', text: 'signin_with', locale: language() === 'th' ? 'th' : 'en' });
    },
    () => box.replaceChildren(h('span', { class: 'hand-hint' }, t('Google sign-in is not available right now.'))),
  );
  return box;
}

/* ---------- sign-in actions ---------- */

/** Signs in with a Google ID token: opens the linked notebook (or makes one) on this device. */
async function signInWithGoogle(app: App, credential: string): Promise<boolean> {
  try {
    const kid = knownKid(app);
    const res = await app.api.googleSignIn(credential, kid ? { lang: language() } : undefined);
    app.api.token = res.token;
    app.mp.disconnect();
    app.store.update(['online'], (s) => (s.online = { playerId: res.player.id, name: res.player.name, token: res.token }));
    void goLive(app);
    await app.cloud.start();
    void syncEntitlements(app);
    app.toast(res.created ? t('Signed in! Your notebook is now saved to your Google account.') : t('Welcome back, {name}!', { name: res.player.name }), 'success');
    return true;
  } catch (err) {
    app.toast(err instanceof Error ? err.message : t('Could not sign in'), 'error');
    return false;
  }
}

/** Links Google to this guest notebook; if that Google account has its own notebook, offers to open it instead. */
async function linkGoogle(app: App, credential: string): Promise<boolean> {
  try {
    const res = await app.api.googleLink(credential);
    app.toast(t('Linked to {email}. Sign in with Google on any device to keep playing.', { email: res.google ?? 'Google' }), 'success');
    return true;
  } catch (err) {
    if (err instanceof ApiError && err.status === 409 && err.message.includes('own notebook')) {
      const open = await confirmBox(
        t('That Google account already has a notebook'),
        t('Open the notebook saved in that Google account on this device instead? You will be asked which notebook to keep.'),
        t('Open it'),
      );
      return open ? signInWithGoogle(app, credential) : false;
    }
    app.toast(err instanceof Error ? err.message : t('Could not link Google'), 'error');
    return false;
  }
}

/** Kid mode, once the age is known (the login screen comes before the age question). */
function knownKid(app: App): boolean {
  return app.store.state.ageGroup !== null && isKid(app.store.state);
}

/** Kids need a grown-up before signing in to a Google account. */
async function grownUpOk(app: App): Promise<boolean> {
  if (!knownKid(app)) return true;
  if (await parentGate(t('Signing in with Google needs a grown-up.'))) return true;
  app.toast(t('That answer was not right. Ask a grown-up to help.'), 'info');
  return false;
}

/** A modal with Google's button; resolves after a successful sign-in/link or when closed. */
async function googleDialog(app: App, mode: 'signin' | 'link'): Promise<boolean> {
  const id = await googleClientId(app);
  if (!id) {
    app.toast(t('Google sign-in is not set up on this server yet.'), 'info');
    return false;
  }
  if (!(await grownUpOk(app))) return false;
  let done = false;
  let closeModal: (v: string) => void = () => {};
  const body = h(
    'div',
    { class: 'account-dialog' },
    h('p', null, mode === 'link' ? t('Link a Google account to this notebook, so you can open it on any device.') : t('Sign in with Google to open your notebook.')),
    googleButton(id, (credential) => {
      void (mode === 'link' ? linkGoogle(app, credential) : signInWithGoogle(app, credential)).then((ok) => {
        done = ok;
        if (ok) closeModal('ok');
      });
    }),
  );
  await modal(mode === 'link' ? t('Link Google account') : t('Sign in with Google'), body, [{ label: t('Cancel'), value: 'close', variant: 'ghost' }], {
    onOpen: (close) => (closeModal = close),
  });
  return done;
}

/* ---------- first start ---------- */

/**
 * The sign-in screen: the first page for a new player, and the menu's
 * "Sign in" button for a notebook that is not saved online yet. Google signs
 * in right away; 'guest' is finished by the caller once the age is known
 * (kid mode decides the name). 'offline' = the server cannot be reached.
 */
export async function welcomeSignIn(app: App, opts: { dismissable?: boolean } = {}): Promise<'google' | 'guest' | 'offline' | 'close'> {
  if (app.store.state.online) return 'google';
  try {
    await app.api.health();
  } catch {
    return 'offline'; // single-player from this browser
  }
  const id = await googleClientId(app);
  const kid = knownKid(app);
  let closeModal: (v: string) => void = () => {};
  const google = id
    ? kid
      ? button(t('Sign in with Google (ask a grown-up)'), {
          color: 'blue',
          sfx: app,
          onClick: () => void googleDialog(app, 'signin').then((ok) => ok && closeModal('google')),
        })
      : googleButton(id, (credential) => void signInWithGoogle(app, credential).then((ok) => ok && closeModal('google')))
    : null;
  const body = h(
    'div',
    { class: 'account-welcome' },
    h('p', null, t('Save your notebook so it is never lost, and open it on any device.')),
    google,
    google ? h('p', { class: 'hand-hint' }, t('Or play as a guest: your notebook is saved too, and you can link Google later in Settings.')) : h('p', { class: 'hand-hint' }, t('Play as a guest: your notebook is saved to the game server too.')),
  );
  const v = await modal(t('Welcome to Paper Legends!'), body, [{ label: t('Play as guest'), value: 'guest', variant: 'primary', color: 'yellow' }], {
    dismissable: opts.dismissable ?? false,
    className: 'account-welcome-card',
    onOpen: (close) => (closeModal = close),
  });
  return v === 'google' || v === 'guest' ? v : 'close';
}

/** The menu's "Sign in" for a notebook that lives only in this browser. */
export async function signInFromMenu(app: App): Promise<void> {
  const v = await welcomeSignIn(app, { dismissable: true });
  if (v === 'guest') await ensureOnline(app, true);
  if (v === 'offline') app.toast(t('Cannot reach the game server. Single-player still works!'), 'error');
}

/* ---------- Settings → Account ---------- */

/** The Account section of Settings. refresh re-renders the settings page. */
export function accountSection(app: App, refresh: () => void): HTMLElement {
  const online = app.store.state.online;
  const status = h('p', { class: 'hand-hint account-status' }, online ? t('Signed in as {name}…', { name: online.name }) : t('Not signed in: this notebook lives only in this browser.'));
  const actions = h('div', { class: 'row-actions' });
  const cloudLine = h('p', { class: 'hand-hint' });
  const showCloud = (): void => {
    cloudLine.textContent =
      { off: '', syncing: t('Saving to the cloud…'), synced: t('Saved to the cloud.'), offline: t('Cloud save will retry when the server is reachable.'), unavailable: t('This server does not keep cloud saves.') }[app.cloud.status] ?? '';
  };
  showCloud();
  app.cloud.on('status', showCloud);

  if (!online) {
    actions.append(
      button(t('Play as guest (save online)'), { small: true, color: 'yellow', sfx: app, onClick: () => void ensureOnline(app, true).then(refresh) }),
      button(t('Sign in with Google'), { small: true, color: 'blue', sfx: app, onClick: () => void googleDialog(app, 'signin').then(refresh) }),
    );
  } else {
    app.api.token = online.token;
    app.api.account().then(
      (a) => {
        if (a.google !== null) {
          status.textContent = t('Signed in with Google ({email}) as {name}.', { email: a.google || 'Google', name: online.name });
          actions.append(button(t('Sign out'), { small: true, variant: 'danger', sfx: app, onClick: () => void signOut(app).then(refresh) }));
        } else {
          status.textContent = t('Guest account ({name}). Link Google so you never lose this notebook.', { name: online.name });
          actions.append(button(t('Link Google account'), { small: true, color: 'blue', sfx: app, onClick: () => void googleDialog(app, 'link').then(refresh) }));
        }
      },
      () => (status.textContent = t('Signed in as {name} (account details need the server).', { name: online.name })),
    );
  }
  return h('div', { class: 'account-section' }, status, cloudLine, actions);
}

/** Signs this device out of a Google-linked notebook; the notebook stays in the account. */
async function signOut(app: App): Promise<void> {
  if (!(await confirmBox(t('Sign out on this device?'), t('Your notebook stays in your Google account. This device starts a fresh notebook until you sign in again.'), t('Sign out')))) return;
  await app.cloud.syncNow();
  app.cloud.stop();
  app.mp.disconnect();
  const fresh = newSave();
  fresh.settings = app.store.state.settings;
  fresh.ageGroup = app.store.state.ageGroup;
  app.store.replace(fresh);
  app.api.token = null;
  app.toast(t('Signed out.'), 'info');
}
